import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Button, Chip, CircularProgress, Tabs, Tab, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
} from '@mui/material';
import {
  ArrowBack, Refresh, OpenInNew, ReceiptLong,
} from '@mui/icons-material';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
} from 'recharts';
import { costProfitService, ProfitCenter360 } from '../../services/costProfitService';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { StatusDot, StatusTone, TableSurface, headerCellSx, bodyRowSx, ErrorState } from '../../components/common/pageKit';

const Kpi: React.FC<{ label: string; value: string; color?: string; sub?: React.ReactNode }> = ({ label, value, color, sub }) => (
  <Box sx={{ minWidth: 110 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>{label}</Typography>
    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: color || 'text.primary', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {sub}
  </Box>
);

const ProfitCenter360Page: React.FC = () => {
  const { id } = useParams();
  const pcId = Number(id);
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const ct = useChartTokens();

  const [data, setData] = useState<ProfitCenter360 | null>(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    costProfitService.profitCenter360(pcId)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load profit centre'))
      .finally(() => setLoading(false));
  }, [pcId]);
  useEffect(() => { load(); }, [load]);

  const drillAccount = (accountId: number) => navigate(`/finance/ledger?account=${accountId}`);
  const drillVoucher = (journalEntryId: number) => navigate(`/finance/journal/${journalEntryId}`);

  if (loading && !data) return <Box sx={{ display: 'grid', placeItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><ErrorState message={error} onRetry={load} /></Box>;
  if (!data) return null;
  const h = data.header;
  const p = data.pnl;
  const marginGood = p.marginVsTarget == null ? (p.margin || 0) >= 0 : p.marginVsTarget >= 0;

  // P&L waterfall rows.
  const pnlRows: { label: string; value: number; kind: 'rev' | 'less' | 'sub' | 'total'; pct?: number | null }[] = [
    { label: 'Revenue', value: p.revenue, kind: 'rev' },
    { label: 'Cost of Goods Sold (5000 / 5100)', value: -p.cogs, kind: 'less' },
    { label: 'Gross Profit', value: p.grossProfit, kind: 'sub', pct: p.grossMargin },
    { label: 'Operating Expenses', value: -p.opex, kind: 'less' },
    { label: 'Operating Profit', value: p.operatingProfit, kind: 'total', pct: p.margin },
  ];

  return (
    <Box sx={{ bgcolor: c.bg, minHeight: '100%' }}>
      {/* Sticky header offset for the app shell */}
      <Box sx={{ position: 'sticky', top: { xs: 32, sm: 64 }, zIndex: 3, bgcolor: c.bg, borderBottom: `1px solid ${c.line}`, px: 3, pt: 2, pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={() => navigate('/finance/cost-centers')}><ArrowBack /></IconButton>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, color: c.purple }}>{h.code}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>{h.name}</Typography>
                <Chip size="small" label={h.type} sx={{ height: 20, fontWeight: 700, bgcolor: `${c.purple}14`, color: c.purple }} />
                <StatusDot label={h.isActive ? 'Active' : 'Inactive'} tone={h.isActive ? 'success' : 'neutral'} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {data.financialYear}{h.segment ? ` · ${h.segment}` : ''}{h.region ? ` · ${h.region}` : ''}{h.managerName ? ` · Manager ${h.managerName}` : ''}{h.plant ? ` · ${h.plant}` : ''}
              </Typography>
            </Box>
          </Stack>
          <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        </Stack>
        {/* KPI row */}
        <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mt: 1.5, rowGap: 1 }}>
          <Kpi label="Revenue" value={formatIndianNumber(p.revenue)} color={c.good} />
          <Kpi label="COGS" value={formatIndianNumber(p.cogs)} />
          <Kpi label="Gross Profit" value={formatIndianNumber(p.grossProfit)} color={c.accent} sub={<Typography variant="caption" color="text.secondary">{p.grossMargin != null ? `${p.grossMargin}% GM` : ''}</Typography>} />
          <Kpi label="Opex" value={formatIndianNumber(p.opex)} />
          <Kpi label="Operating Profit" value={formatIndianNumber(p.operatingProfit)} color={p.operatingProfit >= 0 ? c.good : c.bad} />
          <Kpi label="Margin" value={p.margin != null ? `${p.margin}%` : '—'} color={marginGood ? c.good : c.warn}
            sub={p.targetMargin != null ? <Typography variant="caption" sx={{ fontWeight: 700, color: marginGood ? c.good : c.warn }}>{p.marginVsTarget != null ? `${p.marginVsTarget >= 0 ? '+' : ''}${p.marginVsTarget} vs ${p.targetMargin}% target` : `${p.targetMargin}% target`}</Typography> : undefined} />
        </Stack>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mt: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}>
          {['Overview', 'P&L', 'Monthly Trend', 'Transactions'].map((t) => <Tab key={t} label={t} />)}
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Overview */}
        {tab === 0 && (
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${c.line}`, bgcolor: c.panel }}>
                <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Monthly Revenue, Cost &amp; Profit (Apr-based FY)</Typography>
                <MonthlyChart data={data.monthly} ct={ct} />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${c.line}`, bgcolor: c.panel, height: '100%' }}>
                <Typography sx={{ fontWeight: 700, mb: 1.5 }}>P&amp;L Summary</Typography>
                <Stack spacing={1.25}>
                  {pnlRows.map((r) => (
                    <Stack key={r.label} direction="row" justifyContent="space-between" alignItems="center"
                      sx={{ py: 0.75, borderBottom: r.kind === 'total' ? 'none' : '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="body2" sx={{ fontWeight: r.kind === 'total' || r.kind === 'sub' ? 800 : 500, color: r.kind === 'less' ? c.sub : 'inherit' }}>{r.label}</Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: r.kind === 'total' ? (r.value >= 0 ? c.good : c.bad) : r.kind === 'less' ? c.bad : 'inherit' }}>{formatCurrency(r.value)}</Typography>
                        {r.pct != null && <Typography variant="caption" color="text.secondary">{r.pct}%</Typography>}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* P&L */}
        {tab === 1 && (
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Line', 'Amount', '% of Revenue'].map((hh, i) => <TableCell key={hh} sx={headerCellSx} align={i >= 1 ? 'right' : 'left'}>{hh}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pnlRows.map((r) => (
                    <TableRow key={r.label} sx={{ '& td': { fontWeight: r.kind === 'total' ? 800 : r.kind === 'sub' ? 700 : 500, borderTop: r.kind === 'total' ? `2px solid ${c.line}` : undefined } }}>
                      <TableCell sx={{ color: r.kind === 'less' ? c.sub : 'inherit' }}>{r.label}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: r.kind === 'total' ? (r.value >= 0 ? c.good : c.bad) : r.kind === 'less' ? c.bad : 'inherit' }}>{formatCurrency(r.value)}</TableCell>
                      <TableCell align="right" sx={{ color: c.sub }}>{p.revenue ? `${Math.round((r.value / p.revenue) * 1000) / 10}%` : '—'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Target Operating Margin</TableCell>
                    <TableCell align="right" colSpan={2} sx={{ color: c.sub }}>{p.targetMargin != null ? `${p.targetMargin}%` : 'not set'}{p.marginVsTarget != null && <Chip size="small" label={`${p.marginVsTarget >= 0 ? '+' : ''}${p.marginVsTarget}`} sx={{ ml: 1, height: 20, fontWeight: 700, bgcolor: marginGood ? `${c.good}1f` : `${c.warn}1f`, color: marginGood ? c.good : c.warn }} />}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        )}

        {/* Monthly Trend */}
        {tab === 2 && (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${c.line}`, bgcolor: c.panel }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Monthly Revenue / Cost / Profit &amp; Margin</Typography>
            <MonthlyChart data={data.monthly} ct={ct} height={380} showMargin />
          </Paper>
        )}

        {/* Transactions */}
        {tab === 3 && (
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Date', 'Voucher', 'Account', 'Type', 'Debit', 'Credit', ''].map((hh, i) => (
                      <TableCell key={hh || i} sx={headerCellSx} align={i === 4 || i === 5 ? 'right' : 'left'}>{hh}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lines.map((l) => (
                    <TableRow key={l.id} hover sx={bodyRowSx as any}>
                      <TableCell><Typography variant="caption">{new Date(l.entryDate).toLocaleDateString('en-IN')}</Typography></TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: 'pointer', color: c.accent }} onClick={() => drillVoucher(l.journalEntryId)}>
                          <ReceiptLong sx={{ fontSize: 14 }} />
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{l.entryNumber}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => drillAccount(l.accountId)}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.accountCode} · {l.accountName}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" variant="outlined" label={l.accountType} sx={{ height: 20, fontSize: '0.65rem' }} /></TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{l.debit ? formatCurrency(l.debit) : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{l.credit ? formatCurrency(l.credit) : '—'}</TableCell>
                      <TableCell align="right"><OpenInNew sx={{ fontSize: 15, color: c.sub, cursor: 'pointer' }} onClick={() => drillVoucher(l.journalEntryId)} /></TableCell>
                    </TableRow>
                  ))}
                  {!data.lines.length && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: c.sub }}>No contributing journal lines.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        )}
      </Box>
    </Box>
  );
};

const MonthlyChart: React.FC<{ data: any[]; ct: any; height?: number; showMargin?: boolean }> = ({ data, ct, height = 260, showMargin }) => (
  <Box sx={{ height, width: '100%' }}>
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
        <CartesianGrid {...gridProps(ct)} />
        <XAxis dataKey="month" {...axisProps(ct)} />
        <YAxis yAxisId="l" {...axisProps(ct)} width={54} tickFormatter={(v) => formatIndianNumber(v)} />
        {showMargin && <YAxis yAxisId="r" orientation="right" {...axisProps(ct)} width={40} tickFormatter={(v) => `${v}%`} />}
        <RTooltip {...tooltipStyle(ct)} formatter={(v: any, n: any) => [n === 'Margin' ? `${Number(v)}%` : formatCurrency(Number(v)), n]} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar yAxisId="l" dataKey="revenue" name="Revenue" fill={ct.series(2)} radius={[3, 3, 0, 0]} maxBarSize={22} />
        <Bar yAxisId="l" dataKey="cost" name="Cost" fill={ct.series(1)} radius={[3, 3, 0, 0]} maxBarSize={22} />
        <Line yAxisId="l" type="monotone" dataKey="profit" name="Profit" stroke={ct.series(0)} strokeWidth={2} dot={false} />
        {showMargin && <Line yAxisId="r" type="monotone" dataKey="margin" name="Margin" stroke={ct.series(4)} strokeWidth={2} strokeDasharray="4 3" dot={false} />}
      </ComposedChart>
    </ResponsiveContainer>
  </Box>
);

export default ProfitCenter360Page;
